"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var ajv_1 = __importDefault(require("ajv"));
var ajv_errors_1 = __importDefault(require("ajv-errors"));
var ajv_merge_patch_1 = __importDefault(require("ajv-merge-patch"));
var helper_1 = require("./helper");
var SchemaValidator = /** @class */ (function () {
    /**
     * @param {Object} schema Defines the constraints for all data fields in a data model.
     * @param {Boolean} enableAjvErrors Allows usage of custom error messages defined in the schema
     * @param {Boolean} useDefaults (Optional) Modifies the parent data and inserts valid field values when fields are missing or null when set to true.
     * @param {Boolean} fullFormat (Optional) Allows strict format checking on the Date and Time fields when set to true.
     */
    function SchemaValidator(schema, enableAjvErrors, useDefaults, fullFormat, typeCoercion) {
        this.schema = schema;
        this.enableAjvErrors = enableAjvErrors;
        this.useDefaults = useDefaults;
        this.fullFormat = fullFormat;
        this.typeCoercion = typeCoercion;
        this.ajv = new ajv_1.default(__assign(__assign(__assign({ $data: true, allErrors: true, jsonPointers: true, nullable: true, multipleOfPrecision: 6 }, (this.useDefaults && { useDefaults: 'empty' })), (this.fullFormat && { format: 'full' })), (this.typeCoercion && { coerceTypes: true })));
        ajv_errors_1.default(this.ajv, { singleError: true });
        ajv_merge_patch_1.default(this.ajv);
        this.ajv.addKeyword('decimal', {
            validate: helper_1.validDecimal,
            errors: true,
            metaSchema: {
                type: 'array',
                items: [{ type: 'number' }, { type: 'number' }],
                additionalItems: false,
            },
        });
        this.ajv.addKeyword('formatMinimum', {
            validate: helper_1.validateFormatMinimum,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('formatMaximum', {
            validate: helper_1.validateFormatMaximum,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('formatExclusiveMinimum', {
            validate: helper_1.validateFormatExclusiveMinimum,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('formatExclusiveMaximum', {
            validate: helper_1.validateFormatExclusiveMaximum,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('isNotEmpty', {
            type: 'string',
            validate: function (_sch, data) {
                return typeof data === 'string' && data.trim() !== '';
            },
            errors: true,
        });
        this.ajv.addKeyword('isValidPostalCode', {
            validate: helper_1.validPostalCode,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('isAtLeast90Days', {
            validate: helper_1.isAtLeast90Days,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('isAfterClaimReceivedDate', {
            validate: helper_1.isAfterClaimReceivedDate,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('isYukonOrBritishColumbiaScenario', {
            validate: helper_1.isYukonOrBritishColumbiaScenario,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('isAfterFirstListingDate', {
            validate: helper_1.isAfterFirstListingDate,
            $data: true,
            errors: true,
        });
        this.ajv.addKeyword('isLessThanFirstListingAmount', {
            validate: helper_1.isLessThanFirstListingAmount,
            errors: true,
        });
        this.ajv.addKeyword('hasValidOtherAdjustmentCounts', {
            validate: helper_1.hasValidOtherAdjustmentCounts,
            $data: true,
            errors: true,
        });
        // TODO: This could be removed
        this.ajv.addKeyword('strictlyPastDate', {
            validate: helper_1.validateStriclyPastDate,
            errors: true,
        });
        this.ajv.addKeyword('notAFutureDate', {
            validate: helper_1.validateNotFutureDate,
            errors: true,
        });
        this.ajv.addKeyword('isValidPropertyBedCountUnitCount', {
            validate: helper_1.validatePropertyBedCountUnitCount,
            errors: true,
        });
        this.ajv.addKeyword('isExclusivelyBeforeCurrentDay', {
            validate: helper_1.isExclusivelyBeforeCurrentDay,
            errors: true,
        });
        this.ajv.removeKeyword('multipleOf');
        this.ajv.addKeyword('multipleOf', {
            validate: helper_1.isMultipleOf,
            type: 'number',
            errors: true,
            metaSchema: {
                type: 'number',
            },
        });
        this.ajv.addKeyword('isAfterLastCompleteInstallmentPaidDueDate', {
            validate: helper_1.isAfterLastCompleteInstallmentPaidDueDate,
            $data: true,
            errors: true,
        });
        this.validateFunction = this.ajv.compile(schema); // Create validating function for passed schema
    }
    /**
     * @param {Object|Object[]} schemaDefinitions Defines the constraints for all data fields in a data model or data models.
     * @param {string} key (Optional) Defines the id of the schema.
     */
    SchemaValidator.prototype.addSchema = function (schemaDefinitions, key) {
        this.validateFunction = this.ajv.addSchema(schemaDefinitions, key).compile(this.schema);
    };
    /**
     * @param {Object} extendingSchema Defines the extending schema.
     */
    SchemaValidator.prototype.extendSchema = function (extendingSchema) {
        var extendedSchema = {
            $merge: {
                source: this.schema,
                with: extendingSchema,
            },
        };
        this.validateFunction = this.ajv.compile(extendedSchema);
    };
    /**
     * @param {Object} data Data to truncate based on Schema.
     * @returns {Object} Truncated data.
     */
    // TODO: implement recursive truncate
    SchemaValidator.prototype.truncate = function (data) {
        var _this = this;
        if (data === null ||
            typeof data !== 'object' ||
            typeof data === 'undefined' ||
            Object.keys(data).length === 0) {
            throw new Error('data to truncate is not an object, undefined, empty, or null');
        }
        var newData = JSON.parse(JSON.stringify(data));
        Object.keys(newData).map(function (key) {
            // Will truncate CHAR and VARCHAR data if longer than maxLength
            if (_this.schema.properties[key] != null &&
                _this.schema.properties[key].truncate != null &&
                _this.schema.properties[key].maxLength != null &&
                typeof newData[key] === 'string') {
                newData[key] = newData[key].substring(0, _this.schema.properties[key].maxLength);
            }
            // Will set default if the number is greater than the maximum
            if (_this.schema.properties[key] != null &&
                typeof newData[key] === 'number' &&
                _this.schema.properties[key].maximum != null &&
                Number(_this.schema.properties[key].maximum) < Number(newData[key])) {
                newData[key] = 0;
            }
            // Will set default if the number has too many digits of precision
            if (_this.schema.properties[key] != null &&
                typeof newData[key] === 'number' &&
                Array.isArray(_this.schema.properties[key].decimal) &&
                !helper_1.validateDecimal(_this.schema.properties[key].decimal, newData[key]).isValidDecimal) {
                newData[key] = 0;
            }
            // Will format date or set default if date is invalid
            if (_this.schema.properties[key] != null &&
                _this.schema.properties[key].format === 'date' &&
                typeof newData[key] === 'string' &&
                newData[key].match(/^\d{4}-\d{2}-\d{2}$/g) === null) {
                var defaultDate = Array.isArray(_this.schema.properties[key].type) &&
                    _this.schema.properties[key].type.includes('null')
                    ? null
                    : '1990-01-01';
                newData[key] = defaultDate;
            }
            return newData;
        });
        return newData;
    };
    /**
     * @param {Object} data Data to validate based on Schema.
     * @returns {Void} Void or Errors if validation fails.
     */
    SchemaValidator.prototype.validate = function (data) {
        var schemaValidator = this.validateFunction;
        var isValid = schemaValidator(data);
        // If data is valid, exit function
        if (isValid) {
            return;
        }
        if (schemaValidator.errors != null && schemaValidator.errors.length > 0) {
            if (this.enableAjvErrors) {
                var errorsList = schemaValidator.errors.map(function (error) { return error.dataPath + " " + error.message; });
                throw new Error(errorsList.join(', '));
            }
            else {
                throw schemaValidator.errors;
            }
        }
        else {
            throw new Error('Failed validation and errors were not returned');
        }
    };
    return SchemaValidator;
}());
exports.SchemaValidator = SchemaValidator;
exports.default = SchemaValidator;
